import api from 'api/client';
import { ENDPOINTS } from 'api/endpoints';
import { AddToCartRequest, Cart, CartItem, CartSummary, UpdateCartItemRequest } from 'types/cart';
import catalogConfigService from 'services/catalogConfigService';

type StoreCartItemApi = {
  id?: number;
  Id?: number;
  sku?: string;
  Sku?: string;
  slug?: string;
  Slug?: string;
  name?: string;
  Name?: string;
  quantity?: number;
  Quantity?: number;
  unitPrice?: number;
  UnitPrice?: number;
  listPrice?: number | null;
  ListPrice?: number | null;
  discountPercent?: number | null;
  DiscountPercent?: number | null;
  imageUrl?: string;
  ImageUrl?: string;
};

type StoreCartApi = {
  cartId?: number;
  CartId?: number;
  items?: StoreCartItemApi[];
  Items?: StoreCartItemApi[];
  subtotal?: number;
  Subtotal?: number;
  customerDiscount?: number;
  CustomerDiscount?: number;
  itemCount?: number;
  currency?: string;
  Currency?: string;
  name?: string;
  Name?: string;
  kind?: string;
  Kind?: string;
  isActive?: boolean;
  IsActive?: boolean;
};

type StoreCartSummaryApi = {
  cartId?: number;
  CartId?: number;
  name?: string;
  Name?: string;
  kind?: string;
  Kind?: string;
  isActive?: boolean;
  IsActive?: boolean;
  itemCount?: number;
  ItemCount?: number;
  updatedAt?: string;
  UpdatedAt?: string;
};

const mapItem = (item: StoreCartItemApi): CartItem => {
  const sku = String(item.sku || item.Sku || '').trim();
  const unitPrice = Number(item.unitPrice ?? item.UnitPrice ?? 0);
  const listPrice = Number(item.listPrice ?? item.ListPrice ?? 0);
  const discountPercent = Number(item.discountPercent ?? item.DiscountPercent ?? 0);
  const hasDiscount = discountPercent > 0 && listPrice > unitPrice;
  return {
    lineId: item.id ?? item.Id,
    productId: sku,
    slug: String(item.slug || item.Slug || sku).trim(),
    name: String(item.name || item.Name || sku).trim(),
    quantity: Number(item.quantity ?? item.Quantity ?? 0),
    unitPrice,
    ...(hasDiscount ? { listPrice, discountPercent } : {}),
    imageUrl: item.imageUrl || item.ImageUrl,
  };
};

const mapCart = (data: StoreCartApi): Cart => {
  const items = (data.items || data.Items || []).map(mapItem);
  const net = Number(data.subtotal ?? data.Subtotal ?? items.reduce((s, i) => s + i.unitPrice * i.quantity, 0));
  const lineDiscount = items.reduce(
    (s, i) => s + (i.listPrice ? (i.listPrice - i.unitPrice) * i.quantity : 0),
    0
  );
  const customerDiscount = Number(data.customerDiscount ?? data.CustomerDiscount ?? 0) || 0;
  return {
    items,
    subtotal: net + lineDiscount,
    discount: lineDiscount + customerDiscount,
    total: net - customerDiscount,
    currency: data.currency || data.Currency || catalogConfigService.getDefaultCurrency(),
    cartId: data.cartId ?? data.CartId,
    name: data.name || data.Name || undefined,
    kind: data.kind || data.Kind || undefined,
    isActive: data.isActive ?? data.IsActive,
  };
};

const mapSummary = (data: StoreCartSummaryApi): CartSummary => ({
  cartId: Number(data.cartId ?? data.CartId ?? 0),
  name: String(data.name || data.Name || '').trim() || 'Pedido',
  kind: String(data.kind || data.Kind || 'order').trim() || 'order',
  isActive: Boolean(data.isActive ?? data.IsActive),
  itemCount: Number(data.itemCount ?? data.ItemCount ?? 0),
  updatedAt: String(data.updatedAt || data.UpdatedAt || ''),
});

const cartService = {
  getCart: async (): Promise<Cart> => {
    const response = await api.get<StoreCartApi>(ENDPOINTS.CART);
    return mapCart(response.data);
  },

  addItem: async (data: AddToCartRequest): Promise<Cart> => {
    const response = await api.post<StoreCartApi>(ENDPOINTS.CART_ITEMS, {
      sku: data.productId,
      quantity: data.quantity,
    });
    return mapCart(response.data);
  },

  updateItem: async (data: UpdateCartItemRequest & { lineId?: number }): Promise<Cart> => {
    let lineId = data.lineId;
    if (lineId == null) {
      const current = await cartService.getCart();
      lineId = current.items.find((i) => i.productId === data.productId)?.lineId;
    }
    if (lineId == null) {
      throw new Error('Ítem de carrito no encontrado');
    }

    if (data.quantity <= 0) {
      const response = await api.delete<StoreCartApi>(ENDPOINTS.CART_ITEM(lineId));
      return mapCart(response.data || { items: [] });
    }

    const response = await api.put<StoreCartApi>(ENDPOINTS.CART_ITEM(lineId), {
      quantity: data.quantity,
    });
    return mapCart(response.data);
  },

  clearCart: async (): Promise<void> => {
    await api.delete(ENDPOINTS.CART);
  },

  listCarts: async (): Promise<CartSummary[]> => {
    const response = await api.get<StoreCartSummaryApi[]>(ENDPOINTS.CARTS);
    return (response.data || []).map(mapSummary);
  },

  createCart: async (data: { name?: string; kind?: string } = {}): Promise<Cart> => {
    const response = await api.post<StoreCartApi>(ENDPOINTS.CARTS, data);
    return mapCart(response.data);
  },

  renameCart: async (id: number, name: string): Promise<CartSummary> => {
    const response = await api.put<StoreCartSummaryApi>(ENDPOINTS.CART_BY_ID(id), { name });
    return mapSummary(response.data);
  },

  activateCart: async (id: number): Promise<Cart> => {
    const response = await api.post<StoreCartApi>(ENDPOINTS.CART_ACTIVATE(id));
    return mapCart(response.data);
  },

  cloneCart: async (id: number): Promise<Cart> => {
    const response = await api.post<StoreCartApi>(ENDPOINTS.CART_CLONE(id));
    return mapCart(response.data);
  },
};

export default cartService;
